import React, { useState, useEffect } from 'react';
import { TrendingUp, Download } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';

const IndiaPnLReport: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<any[]>([]);
  const [totals, setTotals] = useState({ total_handling_inr: 0, total_handling_usd: 0, total_gst_collected: 0, count: 0 });
  const [filters, setFilters] = useState({
    travelDateFrom: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    travelDateTo: new Date().toISOString().split('T')[0],
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.travelDateFrom) params.append('travel_date_from', filters.travelDateFrom);
      if (filters.travelDateTo) params.append('travel_date_to', filters.travelDateTo);

      const token = sessionStorage.getItem('jwt_token');
      const res = await fetch(`/api/india-ops/reports/india-pnl?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setRecords(data.data);
        setTotals(data.totals || totals);
      }
    } catch (error) {
      console.error('Error loading India P&L:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">🇮🇳 India P&L Report</h1>
        <p className="text-gray-600">Wynder India revenue from handling fees</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <p className="text-sm text-gray-500">Total Handling Fees (INR)</p>
          <p className="text-2xl font-bold text-green-600">₹{totals.total_handling_inr.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <p className="text-sm text-gray-500">Total Handling Fees (USD)</p>
          <p className="text-2xl font-bold text-green-600">${totals.total_handling_usd.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <p className="text-sm text-gray-500">GST Collected</p>
          <p className="text-2xl font-bold text-blue-600">₹{totals.total_gst_collected.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <p className="text-sm text-gray-500">Bookings</p>
          <p className="text-2xl font-bold text-gray-700">{totals.count}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Travel Date From</label>
            <Input type="date" value={filters.travelDateFrom} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, travelDateFrom: e.target.value })} className="text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Travel Date To</label>
            <Input type="date" value={filters.travelDateTo} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, travelDateTo: e.target.value })} className="text-sm" />
          </div>
          <div className="flex items-end">
            <Button onClick={loadData} disabled={loading} className="text-sm">{loading ? 'Loading...' : 'Search'}</Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Booking</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Guest</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agency</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Travel Date</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Package (INR)</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Handling Fee (INR)</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Handling Fee (USD)</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">GST</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {records.map((r: any) => (
                <tr key={r.booking_id} className="hover:bg-gray-50">
                  <td className="px-3 py-3 text-sm font-medium">{r.itinerary_id}</td>
                  <td className="px-3 py-3 text-sm truncate max-w-[120px]">{r.guest_name}</td>
                  <td className="px-3 py-3 text-sm truncate max-w-[120px]">{r.agency_name}</td>
                  <td className="px-3 py-3 text-sm">{r.travel_date ? new Date(r.travel_date).toLocaleDateString('en-GB') : ''}</td>
                  <td className="px-3 py-3 text-sm text-right">₹{r.package_cost_inr?.toLocaleString('en-IN') || 0}</td>
                  <td className="px-3 py-3 text-sm text-right font-medium text-green-700">₹{r.handling_fee_inr?.toLocaleString('en-IN') || 0}</td>
                  <td className="px-3 py-3 text-sm text-right">${r.handling_fee_usd?.toLocaleString() || 0}</td>
                  <td className="px-3 py-3 text-sm text-right text-blue-600">₹{r.gst_amount?.toLocaleString('en-IN') || 0}</td>
                </tr>
              ))}
            </tbody>
            {records.length > 0 && (
              <tfoot className="bg-gray-50 font-medium">
                <tr>
                  <td colSpan={5} className="px-3 py-3 text-sm text-right">Totals:</td>
                  <td className="px-3 py-3 text-sm text-right text-green-700">₹{totals.total_handling_inr.toLocaleString('en-IN')}</td>
                  <td className="px-3 py-3 text-sm text-right">${totals.total_handling_usd.toLocaleString()}</td>
                  <td className="px-3 py-3 text-sm text-right text-blue-600">₹{totals.total_gst_collected.toLocaleString('en-IN')}</td>
                </tr>
              </tfoot>
            )}
          </table>
          {records.length === 0 && !loading && (
            <div className="text-center py-12 text-gray-500">No records found</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IndiaPnLReport;
